import React, { useState, useCallback } from 'react';
import {
  Button,
  Input,
  InputNumber,
  Space,
  Typography,
  Alert,
  Descriptions,
  message,
  Card,
  Select,
} from 'antd';
import PageTitle from '../../components/PageTitle';
import { mqttPublish, getMqttLatestMessage } from '../../api';
import {
  remotePayloadUseBleDirect,
  buildFinalPayload,
  buildRemoteReadHex,
  buildRemoteSetPowerHex,
  parseMqtt51ReadResponse,
  buildMqttMessagePollOpts,
} from '../../utils/yjMeterRemote';
import { createMqttWS } from '../../utils/mqttWS';

const { Text } = Typography;

const WAIT_MS_WIFI = 50000;

const DEFAULT_TOPIC_ID = '8A9AC230D5E4';
const DEFAULT_METER_ADDR = '8A9A039FB9BC';

const PAYLOAD_FORMAT_OPTIONS = [
  { value: 'hex', label: '纯 hex' },
  { value: 'atAuto', label: 'Plaintext +长度前缀（如 +000A，按字节自动）' },
  { value: 'at34pad', label: 'Plaintext +0034 垫满 52 字节' },
];

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function buildSwitchRawNoCrc(meterKey, action, isBleDirect) {
  const m = String(meterKey || '').trim().toLowerCase();
  if (!isBleDirect && !/^[0-9a-f]{12}$/.test(m)) {
    throw new Error('表地址须为 12 位十六进制');
  }
  const p3 = action === 'open' ? '01' : '02';
  return ((isBleDirect ? '' : m) + '57' + '02' + p3).toLowerCase();
}

function wrapWifiAtPrefix(hexBody) {
  const body = String(hexBody || '').replace(/\s+/g, '').toLowerCase();
  if (!body || body.length % 2 !== 0) throw new Error('hex 长度须为偶数');
  const byteLen = body.length / 2;
  const lenHex = byteLen.toString(16).padStart(4, '0').toUpperCase();
  return `+${lenHex}${body}`;
}

function buildTxPayload(hexPayload, format) {
  const hex = String(hexPayload || '').replace(/\s+/g, '').toLowerCase();
  if (!hex || hex.length % 2 !== 0) throw new Error('hex 长度须为偶数');
  if (format === 'atAuto') return wrapWifiAtPrefix(hex);
  if (format === 'at34pad') {
    const need = 104 - hex.length;
    if (need < 0) throw new Error('指令超过 52 字节');
    return `+0034${hex}${'0'.repeat(need)}`;
  }
  return hex;
}

function stripWifiAtPrefix(raw) {
  const s = String(raw || '').trim().toLowerCase().replace(/\s+/g, '');
  if (/^\+[0-9a-f]{4}[0-9a-f]+$/i.test(s)) return s.slice(5);
  return s;
}

function hex8ToK(hex8) {
  const n = parseInt(String(hex8 || '0'), 16);
  if (!Number.isFinite(n)) return null;
  return (n | 0) / 1000;
}

function parseSwitchStatusFromRaw(rawMsg) {
  const msg = stripWifiAtPrefix(rawMsg);
  if (msg.length < 18) return null;
  const parseEmsw = (hex2) => {
    const n = parseInt(hex2, 16);
    if (n === 1) return '通电';
    if (n === 2) return '断电';
    return null;
  };
  if (msg.substring(12, 14) === '57') return parseEmsw(msg.substring(16, 18));
  if (msg.length >= 60 && msg.substring(12, 14) === '51') return parseEmsw(msg.substring(58, 60));
  if (msg.substring(12, 14) === '54' && msg.length >= 76) return parseEmsw(msg.slice(-6, -4));
  return null;
}

function parse45Remain(rawMsg, candidates) {
  const msg = stripWifiAtPrefix(rawMsg);
  if (msg.length < 26 || msg.substring(12, 14) !== '45') return null;
  const callbackMeterNo = msg.substring(0, 12);
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  const remain = hex8ToK(msg.substring(18, 26));
  if (remain == null) return null;
  return { remain, switchStatus: parseSwitchStatusFromRaw(msg) || '' };
}

function parse54Heartbeat(rawMsg, candidates) {
  const msg = stripWifiAtPrefix(rawMsg);
  if (msg.length < 76 || msg.substring(12, 14) !== '54') return null;
  const callbackMeterNo = msg.substring(0, 12);
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  return {
    remain: hex8ToK(msg.substring(68, 76)),
    total: hex8ToK(msg.substring(60, 68)),
    switchStatus: parseSwitchStatusFromRaw(msg) || '',
    frame: '54',
    fromHeartbeat: true,
  };
}

function topicPairFromId(topicId) {
  const id = String(topicId || '').trim();
  if (!id) return { tx: '', rx: '' };
  return { tx: `/v2/EM/${id}/tx`, rx: `/v2/EM/${id}/rx` };
}

async function publishPayload({ payload, txTopic }) {
  const tx = String(txTopic || '').trim();
  if (!tx) throw new Error('请填写 TX 主题');
  const res = await mqttPublish({ topic: tx, payload, qos: 1 });
  if (!res?.success) {
    throw new Error(res?.msg || res?.error || '下发失败');
  }
  return { detail: tx, payload };
}

const WifiMeterTest = () => {
  const [topicId, setTopicId] = useState(DEFAULT_TOPIC_ID);
  const [txTopic, setTxTopic] = useState(topicPairFromId(DEFAULT_TOPIC_ID).tx);
  const [rxTopic, setRxTopic] = useState(topicPairFromId(DEFAULT_TOPIC_ID).rx);
  const [meterAddr, setMeterAddr] = useState(DEFAULT_METER_ADDR);
  const [payloadFormat, setPayloadFormat] = useState('hex');
  const [manualPayload, setManualPayload] = useState('');
  const [rechargeKwh, setRechargeKwh] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState('');
  const [error, setError] = useState('');
  const [remain, setRemain] = useState(null);
  const [total, setTotal] = useState(null);
  const [switchStatus, setSwitchStatus] = useState('');
  const [lastRaw, setLastRaw] = useState('');
  const [lastPayload, setLastPayload] = useState('');

  const resetResult = useCallback(() => {
    setHint('');
    setError('');
    setRemain(null);
    setTotal(null);
    setSwitchStatus('');
    setLastRaw('');
    setLastPayload('');
  }, []);

  const applyTopicId = (id) => {
    const pair = topicPairFromId(id);
    setTopicId(id);
    setTxTopic(pair.tx);
    setRxTopic(pair.rx);
  };

  const pollRx = async ({ rxTopics, candidates, publishAt, op, tryParse, waitMs }) => {
    const pollOpts = buildMqttMessagePollOpts(meterAddr, op, publishAt);
    const primaryRx = rxTopics[0] || '';
    let parsedFromWs = null;
    let wsHandle = null;
    if (primaryRx) {
      const extraRx = rxTopics.filter((t) => t !== primaryRx);
      wsHandle = createMqttWS(primaryRx, (_t, _m, hex) => {
        const raw = String(hex || _m || '').trim();
        const p = tryParse(raw);
        if (p) parsedFromWs = { ...p, raw };
      }, { extraRxTopics: extraRx });
      try {
        await Promise.race([wsHandle.ready, sleep(3000)]);
      } catch (_) {
        wsHandle = null;
      }
    }

    const deadline = Date.now() + waitMs;
    let lastSeen = '';
    while (Date.now() < deadline) {
      if (parsedFromWs) {
        wsHandle?.close();
        return parsedFromWs;
      }
      const pairs = await Promise.all(
        rxTopics.map(async (topic) => {
          try {
            const msgRes = await getMqttLatestMessage(topic, 'raw', pollOpts);
            return msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
          } catch {
            return '';
          }
        })
      );
      for (let i = 0; i < pairs.length; i++) {
        const trimmed = String(pairs[i] || '').trim();
        if (!trimmed || trimmed === lastSeen) continue;
        lastSeen = trimmed;
        const p = tryParse(trimmed);
        if (p) {
          wsHandle?.close();
          return { ...p, raw: trimmed };
        }
      }
      await sleep(400);
    }
    wsHandle?.close();
    return null;
  };

  const buildTryParse = (candidates) => (raw) => {
    const body = stripWifiAtPrefix(raw);
    const hb = parse54Heartbeat(raw, candidates);
    if (hb) return hb;
    const p51 = parseMqtt51ReadResponse(body, candidates);
    if (p51) return { ...p51, switchStatus: parseSwitchStatusFromRaw(body) || '', frame: '51' };
    const p45 = parse45Remain(raw, candidates);
    if (p45) return { ...p45, frame: '45' };
    const sw = parseSwitchStatusFromRaw(raw);
    if (sw && body.substring(12, 14) === '57') return { switchStatus: sw, frame: '57' };
    if (sw) return { switchStatus: sw };
    return null;
  };

  const publishAndWait = async ({ payload, tx, rx, candidates, opTag, hintPrefix }) => {
    const publishAt = Date.now();
    const pub = await publishPayload({ payload, txTopic: tx });
    setHint(`${hintPrefix}，等待回包最多 ${Math.round(WAIT_MS_WIFI / 1000)} 秒…`);

    const parsed = await pollRx({
      rxTopics: [rx],
      candidates,
      publishAt,
      op: opTag,
      tryParse: buildTryParse(candidates),
      waitMs: WAIT_MS_WIFI,
    });

    if (!parsed) {
      setHint('');
      setError(
        '已下发，但超时未收到 51/45/57 或新 54 心跳。请核对 TX/RX、payload 与表地址；串口未见 MQTTSUB /tx 时下行可能到不了表。'
      );
      return false;
    }

    if (parsed.raw) setLastRaw(parsed.raw);
    if (parsed.remain != null) setRemain(Number(parsed.remain));
    if (parsed.total != null) setTotal(Number(parsed.total));
    if (parsed.switchStatus) setSwitchStatus(parsed.switchStatus);
    const frameHint = parsed.frame ? `（${parsed.frame} 帧）` : parsed.fromHeartbeat ? '（54 心跳）' : '';
    setHint(`完成${frameHint} → ${pub.detail}`);
    return true;
  };

  const runCommand = async (op) => {
    const addr = String(meterAddr || '').trim().toLowerCase().replace(/:/g, '');
    const tx = String(txTopic || '').trim();
    const rx = String(rxTopic || '').trim();
    if (!addr) {
      message.warning('请填写表地址（心跳 payload 前 12 位 hex）');
      return;
    }
    if (!tx || !rx) {
      message.warning('请填写 TX / RX 主题');
      return;
    }

    setLoading(true);
    resetResult();
    setHint('WIFI 表：经云服务器下发 TX，等待 RX 回包（54 心跳约 45 秒一包）…');

    const isBleDirect = remotePayloadUseBleDirect(addr);
    const candidates = [addr];

    let rawNoCrc;
    let opTag;
    try {
      if (op === 'read') {
        rawNoCrc = buildRemoteReadHex(addr, isBleDirect);
        opTag = 'read';
      } else if (op === 'recharge') {
        const kwh = Number(rechargeKwh);
        if (!Number.isFinite(kwh) || kwh < 0) {
          message.warning('请输入有效充值度数');
          setLoading(false);
          return;
        }
        rawNoCrc = buildRemoteSetPowerHex(addr, kwh, isBleDirect);
        opTag = 'recharge';
      } else {
        rawNoCrc = buildSwitchRawNoCrc(addr, op === 'open' ? 'open' : 'close', isBleDirect);
        opTag = op === 'open' ? 'open' : 'close';
      }
    } catch (e) {
      setLoading(false);
      setError(e.message || '组帧失败');
      return;
    }

    const hexPayload = buildFinalPayload(rawNoCrc, true);
    let payload;
    try {
      payload = buildTxPayload(hexPayload, payloadFormat);
    } catch (e) {
      setLoading(false);
      setError(e.message || '组包失败');
      return;
    }

    setLastPayload(payload);
    setManualPayload(payload);

    try {
      const fmtLabel = PAYLOAD_FORMAT_OPTIONS.find((o) => o.value === payloadFormat)?.label || payloadFormat;
      const ok = await publishAndWait({
        payload,
        tx,
        rx,
        candidates,
        opTag,
        hintPrefix: `已下发（${fmtLabel}）`,
      });
      if (ok) {
        message.success({ read: '抄表成功', open: '开闸已回执', close: '关闸已回执', recharge: '充值已回执' }[op] || '完成');
      }
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '操作失败';
      setHint('');
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const runManualPublish = async () => {
    const tx = String(txTopic || '').trim();
    const rx = String(rxTopic || '').trim();
    const payload = String(manualPayload || '').trim().replace(/\s+/g, '');
    if (!tx || !rx) {
      message.warning('请填写 TX / RX 主题');
      return;
    }
    if (!payload) {
      message.warning('请填写手动 payload');
      return;
    }

    setLoading(true);
    resetResult();
    setLastPayload(payload);
    setHint('手动 payload 原样下发…');

    const addr = String(meterAddr || '').trim().toLowerCase().replace(/:/g, '');
    const candidates = addr ? [addr] : [];

    try {
      const ok = await publishAndWait({
        payload,
        tx,
        rx,
        candidates,
        opTag: 'manual',
        hintPrefix: '已手动下发（原样）',
      });
      if (ok) message.success('手动下发完成');
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.response?.data?.error || e?.message || '下发失败';
      setHint('');
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const runReadHeartbeatOnly = async () => {
    const rx = String(rxTopic || '').trim();
    if (!rx) {
      message.warning('请填写 RX 主题');
      return;
    }
    setLoading(true);
    resetResult();
    setHint('仅读取 RX 上最新 54 心跳（不发起下行）…');
    const candidates = [String(meterAddr || '').trim().toLowerCase().replace(/:/g, '')].filter(Boolean);
    try {
      const msgRes = await getMqttLatestMessage(rx, 'raw', {});
      const raw = msgRes?.data?.message ?? msgRes?.data?.hex ?? '';
      const parsed = parse54Heartbeat(raw, candidates);
      if (!parsed) {
        setError('RX 上暂无有效 54 心跳，请确认主题与表在线');
        return;
      }
      setLastRaw(raw);
      if (parsed.remain != null) setRemain(Number(parsed.remain));
      if (parsed.total != null) setTotal(Number(parsed.total));
      if (parsed.switchStatus) setSwitchStatus(parsed.switchStatus);
      setHint('已从 54 心跳读取');
      message.success('读心跳成功');
    } catch (e) {
      setError(e?.message || '读心跳失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageTitle title="wifi电表测试" />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="WIFI 远程表 MQTT 手工测试"
        description="上行 54 心跳以串口 AT+MQTTPUB 为准（+0034 前缀）。下行格式未在串口证实，可在下方切换纯 hex / +长度 / +0034 垫满后分别试。表地址填心跳前 12 位 hex，主题 ID 填 /v2/EM/{ID}/tx|rx 中间段。"
      />

      <Card title="连接参数" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 640 }}>
          <div>
            <Text type="secondary">主题 ID（自动拼 TX/RX）</Text>
            <Input
              value={topicId}
              onChange={(e) => applyTopicId(e.target.value.trim())}
              placeholder={DEFAULT_TOPIC_ID}
              disabled={loading}
            />
          </div>
          <div>
            <Text type="secondary">表地址（payload 前 12 位 hex）</Text>
            <Input
              value={meterAddr}
              onChange={(e) => setMeterAddr(e.target.value)}
              placeholder={DEFAULT_METER_ADDR}
              disabled={loading}
            />
          </div>
          <div>
            <Text type="secondary">TX 主题（下发）</Text>
            <Input value={txTopic} onChange={(e) => setTxTopic(e.target.value)} disabled={loading} />
          </div>
          <div>
            <Text type="secondary">RX 主题（收包）</Text>
            <Input value={rxTopic} onChange={(e) => setRxTopic(e.target.value)} disabled={loading} />
          </div>
          <div>
            <Text type="secondary">下发 payload 格式</Text>
            <Select
              style={{ width: '100%' }}
              value={payloadFormat}
              onChange={setPayloadFormat}
              options={PAYLOAD_FORMAT_OPTIONS}
              disabled={loading}
            />
          </div>
        </Space>
      </Card>

      <Card title="手动指令" size="small" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 640 }}>
          <div>
            <Text type="secondary">payload（原样下发到 TX，可填纯 hex、+000A…、+0034… 等完整内容）</Text>
            <Input.TextArea
              value={manualPayload}
              onChange={(e) => setManualPayload(e.target.value)}
              placeholder="例如 8A9A039FB9BC57020160 或 +000A8a9a039fb9bc57020160"
              disabled={loading}
              rows={3}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
          <Button type="primary" loading={loading} onClick={runManualPublish}>
            手动下发
          </Button>
        </Space>
      </Card>

      <Card title="操作" size="small" style={{ marginBottom: 16 }}>
        <Space wrap align="center">
          <Button loading={loading} onClick={runReadHeartbeatOnly}>
            读54心跳
          </Button>
          <Button type="primary" loading={loading} onClick={() => runCommand('read')}>
            抄表
          </Button>
          <Button loading={loading} onClick={() => runCommand('open')}>
            开闸
          </Button>
          <Button danger loading={loading} onClick={() => runCommand('close')}>
            关闸
          </Button>
          <InputNumber
            min={0}
            step={0.1}
            value={rechargeKwh}
            onChange={(v) => setRechargeKwh(v)}
            disabled={loading}
            addonAfter="kWh"
          />
          <Button loading={loading} onClick={() => runCommand('recharge')}>
            充值
          </Button>
        </Space>
      </Card>

      {lastPayload ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12, wordBreak: 'break-all' }}>
          最近下发：{lastPayload}
        </Text>
      ) : null}
      {hint ? <Alert type="info" showIcon message={hint} style={{ marginBottom: 12 }} /> : null}
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}

      <Descriptions bordered size="small" column={1} style={{ maxWidth: 480 }}>
        <Descriptions.Item label="剩余电量">{remain != null ? `${Number(remain).toFixed(3)} kWh` : '-'}</Descriptions.Item>
        <Descriptions.Item label="总用量">{total != null ? `${Number(total).toFixed(3)} kWh` : '-'}</Descriptions.Item>
        <Descriptions.Item label="开关闸状态">{switchStatus || '-'}</Descriptions.Item>
      </Descriptions>

      {lastRaw ? (
        <Text type="secondary" style={{ display: 'block', marginTop: 12, fontSize: 12, wordBreak: 'break-all' }}>
          最近回包：{lastRaw.slice(0, 200)}{lastRaw.length > 200 ? '…' : ''}
        </Text>
      ) : null}
    </div>
  );
};

export default WifiMeterTest;
